import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { disciplineRatingKeys, disciplineRatingLevelKeys } from "@/lib/query";
import type { DisciplineRatingLevelRow } from "@/schemas/discipline-rating-levels";

// One level of a Discipline's fixed 1–5 rating scale, with optional help text describing
// what the level means. Authored in Configuration; surfaced on the Resource page as hover
// guidance. The rating VALUE (per Resource) is a separate concept — see use-discipline-
// ratings.ts. The Discipline Rating feeds the Expectation dimension of Fit Score.
export type DisciplineRatingLevel = DisciplineRatingLevelRow;

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * The fixed 1–5 rating scale + per-level help text for one Discipline. Gated on `enabled`
 * so the config drill-in dialog defers the fetch until it opens (frontend.md dialog rule).
 */
export function useDisciplineRatingLevels(
  disciplineId: string | null,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: disciplineRatingLevelKeys.list(disciplineId ?? ""),
    queryFn: () =>
      jsonFetch<DisciplineRatingLevel[]>(`/api/disciplines/${disciplineId}/rating-levels`),
    enabled: (opts.enabled ?? true) && !!disciplineId,
  });
}

/**
 * Author the per-level help text for a Discipline's scale (Configuration, RM/CEO). On
 * success, refresh the editor's own data AND the per-Resource rating queries so the
 * rating-capture tooltips on the Resource page pick up the new guidance immediately.
 */
export function useUpdateDisciplineRatingLevels(disciplineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (levels: DisciplineRatingLevel[]) =>
      jsonFetch<DisciplineRatingLevel[]>(`/api/disciplines/${disciplineId}/rating-levels`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ levels }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: disciplineRatingLevelKeys.list(disciplineId) });
      // Resource-page rating tooltips read the level help text via the discipline-ratings
      // payload — refresh those so authored guidance shows without a reload.
      qc.invalidateQueries({ queryKey: disciplineRatingKeys.all });
      toast.success("Rating levels updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
